import { useState, useEffect, useCallback } from "react";
import { getPwaState, setPwaState, type SwStatus } from "@/lib/pwa-update";

export interface PwaUpdateInfo {
  version: string;
  needsUpdate: boolean;
  swStatus: SwStatus;
  update: () => void;
}

export function usePwaUpdate(): PwaUpdateInfo {
  const state = getPwaState();
  const [swStatus, setSwStatus] = useState<SwStatus>(state?.state ?? "idle");
  const [needsUpdate, setNeedsUpdate] = useState(state?.needsUpdate ?? false);

  const refresh = useCallback(() => {
    const s = getPwaState();
    if (!s) return;
    setSwStatus(s.state);
    setNeedsUpdate(s.needsUpdate);
  }, []);

  useEffect(() => {
    refresh();
    const handler = () => refresh();
    window.addEventListener("zappy-pwa-state-change", handler);
    return () => window.removeEventListener("zappy-pwa-state-change", handler);
  }, [refresh]);

  const update = useCallback(() => {
    const s = getPwaState();
    if (s?.update) {
      s.update();
    } else {
      window.location.reload();
    }
  }, []);

  return {
    version: state?.version ?? (import.meta.env.VITE_APP_VERSION as string | undefined) ?? "dev",
    needsUpdate,
    swStatus,
    update,
  };
}
